import React, { useMemo, useRef, useState } from 'react';

import {
  DISCHARGE_TYPE,
  DISCHARGE_TYPE_LABELS,
  DISCHARGE_ERROR_CODE,
  ADMISSION_STATUS,
  DECEASED_FROM_STATUSES,
  isDischargeStartable,
  hasOpenDeathRecord,
  isDeathOutcome,
  isMoneyHidden,
  DOCTOR_REQUIRED_DISCHARGE_COPY,
  ADMISSION_ACCESS_ERROR_CODE,
  NOT_TREATING_DOCTOR_DISCHARGE_COPY,
  dodIsConfirmed,
  MOVE_CHARGES_REASON,
  MOVE_CHARGES_REASON_OPTIONS,
  FIX_CHARGES_ACTION,
  FIX_CHARGES_BLOCKED_CODE,
  FIX_CHARGES_RELOAD_CODES,
  FIX_CHARGES_RELOAD_COPY,
  fixChargesErrorCopy,
} from '../../../constants/admission';
import { formatPaise } from '../../../utils/money';
import { diagnosticsBillLabel } from '../../../hooks/useDiagnosticsBillPrint';
import ConfirmationModal from '../../common/ConfirmationModal/ConfirmationModal';
import TakeDepositDialog from '../bill/TakeDepositDialog';
import ReturnAdvanceDialog from '../bill/ReturnAdvanceDialog';
import { STAFF_ROLE_LABELS } from '../../../constants/staff';
import { useScope } from '../../../context/PermissionContext';
import {
  buildDischargeGate, dischargeErrorMessage, buildCompleteConfirmation, buildSuccessMessage,
  resolveDischargeDateTimeIso, defaultConfirmDodFields, confirmDodFieldsToIso, validateConfirmedDod,
  DOD_NO_PERMISSION_REASON, DOD_DECEASED_LOCK_REASON, dodInitiatedToast, dodCompletedToast,
  DOD_INITIATE_HELPER, DOD_WALK_OUT_HEADING, DOD_WALK_OUT_REASON_LABEL, DOD_WALK_OUT_FINAL_BANNER,
  DOD_REASON_LABEL, DOD_REASON_MAX_LENGTH, dodBackdateHint, dodDaysBack,
  buildInitiateBody, initiateConfirmLines, LATE_COMPLETE_NOTE, chargesAfterDischargeDateItems,
  lateChargesBodyLine, LATE_CHARGES_HINT, labDoneLine, defaultLineActions, fixChargesCounts, fixChargesButtonCount, buildFixChargesBody,
} from './dischargeModel';
import { REASON_OTHER, SIGNER_OPTIONS, reasonOptionsFor, resolveDodReason } from './reasonOptions';
import { getStoredSession } from '../../../services/api/sessionApi';
import { formatDischargeDateTimeIST } from './planDischargeModel';

const overlayCls = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';
const panelCls = 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl w-full max-w-xl max-h-[92vh] overflow-y-auto';
const btnPrimary = 'px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60';
const btnSecondary = 'px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm';
const inputCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm';
const labelCls = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';

const TYPE_OPTIONS = [
  DISCHARGE_TYPE.NORMAL,
  DISCHARGE_TYPE.LAMA,
  DISCHARGE_TYPE.DAMA,
  DISCHARGE_TYPE.DECEASED,
  DISCHARGE_TYPE.REFERRED,
];

const typeChipOn = 'px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border bg-teal-600 border-teal-600 text-white';
const typeChipOff = 'px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700';
// death-record slice (invariant 13/D40) — LOCKED, never silently disabled:
// still visible, still states why, just not clickable.
const typeChipLocked = 'px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-400 dark:text-slate-500 cursor-not-allowed';
// [NEEDS-PO] — pasted from the ratified mockup (variant-a-header-link.html state 4).
const DEATH_TYPE_LOCKED_REASON = '🔒 Locked — a death is recorded on this admission. Remove it on Manage Admission to choose a different discharge type.';

const emptyCauseOfDeath = { immediate_cause: '', antecedent_cause: '', death_at: '' };


/**
 * DischargeDialog (C5 · FE-2, R3-4 · S4) — "Discharge patient". ONE in-app
 * modal driving every discharge type through the frozen contract (#1-#3 +
 * R3-4 EP-1): select type → the HARD zero-balance gate (hands off to the
 * REUSED C4 TakeDepositDialog + the BOUNDED ReturnAdvanceDialog) →
 * type-specific fields → Complete.
 *
 * death-record slice (FE-6, invariant 13 — THE WORKED EXAMPLE this design
 * model is built around) — while `hasOpenDeathRecord(admission)`, the type is
 * auto-picked DECEASED and LOCKED; the other four render locked WITH A
 * VISIBLE REASON (D40), never silently disabled. The death block is SPLIT:
 * `death_at` + the declaring doctor render read-only, pre-filled from the
 * record; `immediate_cause`/`antecedent_cause` stay EDITABLE — the record
 * dialog (FE-1, Variant A) never captures a cause and `CAUSE_OF_DEATH_REQUIRED`
 * still fires at complete, so a wholly read-only block would make the
 * admission permanently un-dischargeable. Declaring-doctor keys are DISPLAY
 * only — never re-submitted at complete (EP-1's wire shape is unchanged).
 *
 * R3-4 [REVERSAL-3] retired the old soft "record as dues" path
 * (DISCHARGE_WITH_DUES): every discharge type must now settle to ₹0 (charges
 * + unsettled lab-credit + any excess advance refunded). LAMA/DAMA/DECEASED
 * alone may bypass a POSITIVE residual via a permissioned write-off
 * exception (WRITE_OFF_DUES — reuses the existing Payment Pending queue
 * slug, no new one) — an authorised actor sees a "Discharge with write-off
 * (exception)" form (Reason + Authorised by, both required); a
 * non-permissioned actor sees the same "settle to discharge" hard block as
 * NORMAL/REFERRED, never the exception form (AC-NEG).
 *
 * bk-584 REVERSED (owner ruling 2026-08-29, bk-781) — DECEASED used to be
 * the ONE exception here: a death with a positive residual was completable
 * by ANY holder of DISCHARGE_PATIENT, no write-off, no authoriser, no
 * reason, the debt staying owed (dues_status=OPEN), never written off. The
 * owner reversed this — in practice there was no usable way to collect
 * against a deceased discharge, so that residual was effectively
 * unrecoverable. DECEASED now goes through the SAME write-off form as
 * LAMA/DAMA above; a death at 02:00 with unpaid dues is BLOCKED until an
 * admin authorises a write-off (deliberate, owner-accepted).
 *
 * FIX-3 TWO-STEP (Wave-2, owner 2026-08-04) — discharge is a VISIBLE two-step:
 *   STEP 1 · "Initiate discharge" (its OWN action, shown while the admission is
 *     ADMITTED/IN_TREATMENT) calls `onInitiate` ONLY → the server mints the
 *     invoice number (INV#) + stamps `discharge_initiated_at` and the admission
 *     flips to DISCHARGE_INITIATED. The document title flips Interim Bill (IP
 *     number) → Invoice (INV#), driven off the response's `invoice_number_printed`
 *     (non-empty ⇒ minted). The bill STAYS editable in DISCHARGE_INITIATED.
 *   STEP 2 · "Complete discharge" (shown once DISCHARGE_INITIATED/BILLING_CLEARANCE)
 *     runs clear→complete (NO initiate leg) + the settle/write-off gate, then
 *     frees the bed.
 * DECEASED (bk-752 — recordable at any stage of the walk) always completes
 * DIRECTLY, with no initiate/clear leg, from whichever of the four statuses
 * it is picked at — `DECEASED_FROM_STATUSES` (`constants/admission.js`) is
 * the shared, table-mirrored set the type picker offers it from. A `busy`
 * guard + the ConfirmationModal (never a
 * native dialog) makes a double-click act exactly once.
 *
 * admission-access (S4-AC6) — `money_visible === false` (a doctor who is not the
 * primary): the parent passes a NULL `balancePaise` and never fetches the bill.
 * ONE flag (`moneyHidden`, below) drives the whole money-free path: the gate is
 * fed a `null` balance (it predicts nothing from it — the SERVER judges the
 * balance; `gate.balanceKnown === false`), no balance / collect / refund / write-off / dues surface renders,
 * the bill documents (invoice, diagnostics bill, dues acknowledgement — all
 * money-only routes) are not offered, and amount-bearing refusals map to one
 * amount-free line. The initiate / clear / complete steps still run.
 *
 * The balance shown is the parent's cached `balancePaise` (single source),
 * refreshed ONLY from a written server value: a successful Collect/Return
 * advance response, or the `balance_paise` extra on a 409
 * BALANCE_CHANGED/BALANCE_NOT_ZERO/DUES_NEGATIVE_BALANCE — never re-derived.
 * Unsettled lab-credit is NOT pre-fetched here (no FE data source exists
 * outside the Bill tab's own SettleCreditsCard) — it surfaces reactively from
 * a 409 UNSETTLED_LAB_CREDIT attempt, carrying the breakdown entries.
 *
 * @param admission                    { admission_id, patient_name, ip_number, bed_label, ward_name, admitted_at, status,
 *                                        cause_of_death?, death_rev?, planned_discharge_at? } — the caller keeps
 *                                       `cause_of_death`/`death_rev` fresh with no refetch (S5); this is what
 *                                       `hasOpenDeathRecord` reads to lock. `planned_discharge_at` (S4, C2) drives the
 *                                       Complete-step confirm line + the AC-I8/9/10 guards (FE-4) — single source,
 *                                       never re-derived here.
 * @param balancePaise                 server-cached signed balance at open (single source)
 * @param refundableDeposits           [{payment_id, receipt_number, method, refundable_remaining_paise}], oldest-first
 * @param canReturnAdvance             holds RETURN_ADVANCE (reception + admin — owner redesign)
 * @param canWriteOffDues               holds WRITE_OFF_DUES (ADMIN-only; R3-4 reuses this slug for the
 *                                       discharge-time LAMA/DAMA/DECEASED write-off exception — bk-584
 *                                       reversed 2026-08-29: DECEASED is no longer exempt)
 * @param canPrint                     holds GENERATE_IPD_BILL_PDF
 * @param authorisers                  [{member_id, name, role}] active staff for the write-off
 *                                       "Authorised by" picker (O3 — replaces the free-text field)
 * @param canRecordDischargeDate       S1 AC-NEG — holds the discharge permission. `false`
 *                                       renders the Initiate confirm step READ-ONLY with the
 *                                       ratified reason and the button disabled; never a
 *                                       silent no-op. Defaults to true (the caller already
 *                                       gates opening this dialog).
 * @param onClose
 * @param onInitiate(body)             → { success, data?: AdmissionResponse, error?, code? }
 *        (data carries `status`, `invoice_number_printed`, `discharge_initiated_at`,
 *        `discharge_date_confirmed_at`). S1 — `body` is the OPTIONAL C1 request
 *        `{ discharge_at }` carrying the CONFIRMED discharge date the desk approved on the
 *        confirm step; omitted entirely for LAMA/DAMA (the server records the actual
 *        walk-out moment, today's carve-out).
 * @param onInitiated(admission)       optional — fired after a successful initiate with the
 *                                       FULL AdmissionResponse, so the parent can merge the
 *                                       confirmed date + its marker into its own row IN THE
 *                                       SAME RENDER (no refetch, no request storm)
 * @param onClear()                    → { success, data?, error?, code? }
 * @param onComplete(body)             → { success, data?: {admission, bill, dues, replayed}, error?, code?, ...extras }
 * @param onCollectDeposit(body)       → { success, data?, error?, code? } (calls collectDeposit)
 * @param onReturnAdvance(paymentId, body) → { success, data?, error?, code? } (calls returnAdvance;
 *                                       body = { method, reference?, idempotency_key })
 * @param onPrintInvoice / onPrintSummary / onPrintDuesAck  () → opens the respective PDF
 *        (onPrintInvoice opens the unified Invoice at its FINAL state — D1)
 *        (onPrintSummary(title) — Follow-on bk-1254/FIX-7-FE: takes the
 *        'Discharge summary'/'Death summary' title THIS dialog derives from
 *        the just-completed outcome; resolves {success, error} for `runPrint`
 *        to drive the SAME busy/error chrome as onPrintInvoice/onPrintLabBill)
 * @param onPrintLabBill               optional () → opens the consolidated Diagnostics Bill PDF
 *        (Wave-3 FE-2 — same GENERATE_IPD_BILL_PDF gate as onPrintInvoice)
 * @param onAssignDoctor               optional () → opens the existing "Change primary doctor"
 *        dialog (owner ruling H). Read straight off `admission.primary_doctor_member_id`
 *        (denormalized on the row, same field `AdmissionWorkspace`'s own "Primary doctor"
 *        line reads) — no fetch, single source. Rendered only when the caller supplies it.
 */
export default function DischargeDialog({
  admission,
  hasDiagnostics,
  balancePaise,
  refundableDeposits = [],
  canReturnAdvance = false,
  canWriteOffDues = false,
  canPrint = false,
  canRecordDischargeDate = true,
  authorisers = [],
  onClose,
  onInitiate,
  onInitiated,
  onFixCharges,
  onClear,
  onComplete,
  onCollectDeposit,
  onReturnAdvance,
  onPrintInvoice,
  onPrintSummary,
  onPrintDuesAck,
  onPrintLabBill,
  onAssignDoctor,
}) {
  const moneyHidden = isMoneyHidden(admission);
  const [balance, setBalance] = useState(Math.round(Number(balancePaise) || 0));
  // The bill documents are money-only routes (403 for a hidden caller): offered
  // to nobody who cannot open them.
  const invoicePrint = moneyHidden ? undefined : onPrintInvoice;
  const labBillPrint = moneyHidden ? undefined : onPrintLabBill;
  const duesAckPrint = moneyHidden ? undefined : onPrintDuesAck;
  // FIX-3 — the admission's status is tracked LOCALLY so a successful Initiate
  // flips the dialog from step 1 → step 2 in-place (the parent row denorm is
  // stale until it refetches). Seeded from the row; updated from the initiate
  // response's `status`.
  const [currentStatus, setCurrentStatus] = useState(admission.status);
  // Document-title flip driver: the minted `invoice_number_printed` (non-empty
  // ⇒ Interim Bill → Invoice). Seeded from the row (a resumed DISCHARGE_INITIATED
  // admission already carries it), updated from the initiate response.
  const [invoiceNumberPrinted, setInvoiceNumberPrinted] = useState(admission.invoice_number_printed || null);
  // death-record slice (invariant 13, FE-6) — hasOpenDeathRecord reads OFF
  // the `admission` prop (the caller keeps it fresh with no refetch, S5), not
  // local state: a death recorded on THIS still-open dialog session (were
  // that ever possible) would flip this on the very next render.
  const deathLocked = hasOpenDeathRecord(admission);
  // Owner ruling H (2026-09-13, decisions/discharge-templates-phase1
  // addendum H) — no discharge without a treating (primary) doctor on the
  // admission. `primary_doctor_member_id` is the SAME denormalized field
  // `AdmissionWorkspace`'s "Primary doctor" line already reads (single
  // source) — no fetch here.
  const doctorMissing = !admission.primary_doctor_member_id;
  // S4 (FE-4, AC-I9) — the single-source "is this actor an admin" selector
  // (PermissionContext `useScope`), the SAME one `RunningBillPanel` already
  // reads for its own admin-bypass gate. Mirrors the server's role check
  // (`ipd_charge_service.py:586` pattern) — role-based, never a permission
  // slug the role merely happens to hold.
  const { isAdmin, role } = useScope();
  const [dischargeType, setDischargeType] = useState(() => (deathLocked ? DISCHARGE_TYPE.DECEASED : null));
  const [pendingType, setPendingType] = useState(null); // type change awaiting the "clear fields?" confirm
  const [reason, setReason] = useState('');
  // The SPLIT block (invariant 13): `death_at` seeds from the RECORD and is
  // never edited by the desk when `deathLocked` — it rides in `buildCompleteBody`
  // unchanged (idempotent merge, invariant 14). `declaring_doctor_*` is
  // DISPLAY only and is never part of this state — it is never submitted at
  // complete (EP-1's wire shape is unchanged: `cause_of_death` carries only
  // immediate_cause/antecedent_cause/death_at, same as before this slice).
  const [causeOfDeath, setCauseOfDeath] = useState(() => (
    deathLocked
      ? { ...emptyCauseOfDeath, death_at: (admission.cause_of_death && admission.cause_of_death.death_at) || '' }
      : emptyCauseOfDeath
  ));
  const [referralReason, setReferralReason] = useState('');
  const [referredTo, setReferredTo] = useState('');
  const [authorisedByMemberId, setAuthorisedByMemberId] = useState(''); // O3 staff picker selection
  const [moneyDialog, setMoneyDialog] = useState(null); // 'collect' | 'refund' | null
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [errorEntries, setErrorEntries] = useState(null); // UNSETTLED_LAB_CREDIT breakdown, if any
  const [lateCharges, setLateCharges] = useState(null); // CHARGES_AFTER_DISCHARGE_DATE lines, if any
  // Owner 2026-10-13 — "Move these charges to the discharge date": the
  // refusal's own `discharge_date` (the server's IST day) + the reason pick.
  const [lateDod, setLateDod] = useState(null);
  const [lineActions, setLineActions] = useState({}); // charge id → 'MOVE' | 'REMOVE'
  const [lateNotice, setLateNotice] = useState(null); // plain message after a stale-list reload
  const [moveReason, setMoveReason] = useState('');
  const [moveOther, setMoveOther] = useState('');
  const [moveError, setMoveError] = useState(null);
  const moveInFlight = useRef(false); // single-flight across a same-tick double click
  // BUGFIX/lama-discharge round 2 (R1 sticky reveal) — once a refusal tells
  // us THIS dues-eligible, zero-cash-balance discharge is absorbing
  // unsettled lab credit as a write-off, both flags STICK (never reset) so
  // the authoriser picker / reason field stay revealed across a re-render
  // and a stale submit. `unsettledLabPaise` (the exact amount) is separate
  // and may stay null — `AuthorisedByRequired` carries no extras.
  const [labWriteOffPending, setLabWriteOffPending] = useState(false);
  const [unsettledLabPaise, setUnsettledLabPaise] = useState(null);
  const [success, setSuccess] = useState(null); // { admission, bill, dues, replayed }
  // ── S1 Screen A — the CONFIRMED discharge date the desk approves at
  //    Initiate. Derived ONCE per mount from the admission's own plan (else
  //    `now`, IST) — never re-derived while the dialog is open, so a plan
  //    moving elsewhere can never overwrite what the desk already typed
  //    (Screen A's REVERSE rule); re-opening the dialog re-derives. The
  //    logged-in user's clock is only ever the NO-PLAN default, never dressed
  //    up as "the plan".
  const [dodFields, setDodFields] = useState(() => defaultConfirmDodFields(admission));
  // LAMA/DAMA "When did the patient leave?" — Now (default) or Earlier (the date fields).
  const [walkOutEarlier, setWalkOutEarlier] = useState(false);
  const [nowIso] = useState(() => new Date().toISOString()); // display of "Now" only
  // Backdate reason PICK-LIST: a picked option key (sentence filled from the date) or Other (typed).
  const [dodReasonKey, setDodReasonKey] = useState('');
  const [dodReasonOther, setDodReasonOther] = useState('');
  const isWalkOut = dischargeType === DISCHARGE_TYPE.LAMA || dischargeType === DISCHARGE_TYPE.DAMA;
  const walkOutNow = isWalkOut && !walkOutEarlier;
  // Deps are PRIMITIVES only — the `admission` object is never a dep.
  const dodIso = useMemo(
    () => (walkOutNow ? nowIso : confirmDodFieldsToIso(dodFields.date, dodFields.time)),
    [walkOutNow, nowIso, dodFields.date, dodFields.time],
  );
  const dodDays = dodDaysBack(dodIso);
  const [dodSigner, setDodSigner] = useState(''); // LAMA form signed by: patient | attendant
  const [staffName] = useState(() => (getStoredSession()?.displayName || '').trim());
  // Options depend on type + days back; a pick that the current date/type no longer offers drops out.
  const reasonOptions = reasonOptionsFor({ type: dischargeType, daysBack: dodDays, staff: staffName });
  const pickedKey = dodReasonKey === REASON_OTHER || reasonOptions.some((o) => o.key === dodReasonKey) ? dodReasonKey : '';
  const dodReason = dodDays >= 1
    ? resolveDodReason(pickedKey, dodReasonOther, dodIso, { signer: dodSigner, staff: staffName }).text
    : '';
  const dodCheck = useMemo(() => validateConfirmedDod({
    valueIso: dodIso,
    admittedAt: admission.admitted_at,
    isAdmin,
    fyAnchor: admission.invoice_fy,
    reason: dodReason,
    walkOut: isWalkOut,
  }), [dodIso, admission.admitted_at, admission.invoice_fy, isAdmin, dodReason, isWalkOut]);
  // The ratified confirm sentence + toast for the just-recorded date.
  const [initiatedAt, setInitiatedAt] = useState(null); // ISO of the date actually recorded

  // bk-110 — the diagnostics-bill CTA is disabled when the stay has no
  // diagnostics. `has_diagnostics` rides the RUNNING-BILL payload, not the
  // admission row, so the parent (AdmissionWorkspace) threads it in as the
  // `hasDiagnostics` prop. Fails OPEN: only an explicit `false` disables it, so
  // an undefined field (bill still loading / pre-integration) never blocks it.
  const diagnosticsBillDisabled = hasDiagnostics === false;

  // bk-112 — Print invoice / Generate Diagnostics bill both silently dropped a
  // failed `{success:false, error}` result. `runPrint` gives each a per-button
  // loading state + surfaces the error on a visible inline banner (same
  // pattern as the `discharge-error` banner above), and single-flight-guards
  // against a double-click via a ref (state alone can lag a rapid 2nd click).
  const [printing, setPrinting] = useState({ invoice: false, labBill: false, summary: false });
  const printingRef = useRef({ invoice: false, labBill: false, summary: false });
  const [printError, setPrintError] = useState(null);
  const runPrint = async (kind, fn) => {
    if (!fn || printingRef.current[kind]) return;
    printingRef.current[kind] = true;
    setPrinting((s) => ({ ...s, [kind]: true }));
    setPrintError(null);
    const res = await fn();
    printingRef.current[kind] = false;
    setPrinting((s) => ({ ...s, [kind]: false }));
    if (res && res.success === false) setPrintError(res.error || 'Could not open the document.');
  };
  const handlePrintInvoiceClick = () => runPrint('invoice', invoicePrint);
  // The freshest status this dialog itself knows — its LOCAL `currentStatus`
  // (flipped synchronously the instant Initiate succeeds, FIX-3) pre-Complete,
  // or the just-completed `success.admission.status` after — never the
  // parent desk's admission-row prop, which lags a refetch behind either.
  // F1's confirm-before-mint decision binds to THIS, not a status captured
  // once at mount.
  const labBillStatus = success ? success.admission.status : currentStatus;
  const handlePrintLabBillClick = () => runPrint('labBill', labBillPrint && (() => labBillPrint(labBillStatus)));
  // Follow-on bk-1254 (FIX-7-FE) — the post-complete summary print reuses the
  // SAME `runPrint` single-flight + `printError` banner as invoice/lab-bill
  // above (one convention, not a forked copy). `success.admission.status`
  // (the just-completed outcome) is the ONLY place this dialog derives
  // death-vs-discharge from post-complete — same `isDeathOutcome` selector
  // every other surface uses, never re-derived by string comparison.
  const handlePrintSummaryClick = () => runPrint('summary', onPrintSummary && (() => onPrintSummary(
    isDeathOutcome(success && success.admission && success.admission.status) ? 'Death summary' : 'Discharge summary',
  )));

  // ── Two-step gate: step 1 (Initiate) while the admission is still admitted /
  //    in-treatment, EXCEPT DECEASED (bk-752), which always completes
  //    directly with no initiate/clear leg, from any of the four statuses ──
  const inInitiateStep = isDischargeStartable(currentStatus) && dischargeType !== DISCHARGE_TYPE.DECEASED;
  // The Initiate date block shows for every non-death type (LAMA/DAMA word it
  // "When did the patient leave?"); DECEASED never reaches this step.
  const dodConfirmVisible = inInitiateStep && !!dischargeType;

  // bk-752 — offer Death ONLY at a status the server will actually accept
  // it from (`DECEASED_FROM_STATUSES`, the shared table-mirrored set) —
  // never a flat list that offers a type the current status would 409 on.
  // Every other type is unaffected — this dialog only ever opens at a
  // status where NORMAL/LAMA/DAMA/REFERRED are already valid.
  //
  // death-record slice (invariant 4/13, D40) — once a death record exists,
  // ALL FIVE render: DECEASED enabled (auto-picked, above), the other four
  // LOCKED WITH A VISIBLE REASON, never silently hidden/disabled (the
  // `DECEASED_FROM_STATUSES` filter would otherwise still offer them as
  // ordinary clickable chips).
  const offeredTypeOptions = deathLocked
    ? TYPE_OPTIONS
    : TYPE_OPTIONS.filter((t) => t !== DISCHARGE_TYPE.DECEASED || DECEASED_FROM_STATUSES.has(currentStatus));
  const invoiceMinted = !!(invoiceNumberPrinted && String(invoiceNumberPrinted).trim());

  const hasEnteredFields = () => (
    reason.trim() || referralReason.trim() || referredTo.trim() || authorisedByMemberId.trim()
    || causeOfDeath.immediate_cause.trim() || causeOfDeath.antecedent_cause.trim() || causeOfDeath.death_at.trim()
  );

  const clearTypeFields = () => {
    setReason('');
    setCauseOfDeath(emptyCauseOfDeath);
    setReferralReason('');
    setReferredTo('');
    setAuthorisedByMemberId('');
  };

  const pickType = (type) => {
    // death-record slice (invariant 13) — the locked chips are `disabled` in
    // the render below; this is defence in depth against a programmatic call.
    if (deathLocked && type !== DISCHARGE_TYPE.DECEASED) return;
    if (type === dischargeType) return;
    if (dischargeType && hasEnteredFields()) { setPendingType(type); return; }
    setDischargeType(type);
    clearTypeFields();
  };
  const confirmTypeChange = () => { setDischargeType(pendingType); clearTypeFields(); setPendingType(null); };
  const keepCurrentType = () => setPendingType(null);

  // Integration reconcile (R20): the server's Complete stamps the CONFIRMED
  // DOD only when `discharge_date_confirmed_at` is set (a legacy DI row whose
  // `planned_discharge_at` is still an unconfirmed plan completes at NOW) —
  // mirror that, so the dialog never shows a plan the server will not stamp.
  const confirmedDodIso = dodIsConfirmed(admission) ? (admission.planned_discharge_at || null) : null;
  const gate = useMemo(() => buildDischargeGate({
    dischargeType,
    // admission-access — a withheld balance is `null` to the gate, whatever the
    // local number holds: the ONE place the money-hidden gate lives.
    balancePaise: moneyHidden ? null : balance,
    reason,
    causeOfDeath,
    referralReason,
    referredTo,
    authorisedByMemberId,
    canWriteOffDues,
    labWriteOffPending,
    // DOD rule v3 — post-initiate `planned_discharge_at` IS the confirmed
    // date (the value is stored on that attribute; `discharge_date_confirmed_at`
    // carries its meaning). Single source, never re-derived here.
    confirmedDischargeAt: confirmedDodIso,
    admittedAt: admission.admitted_at,
    fyAnchor: admission.invoice_fy,
    isAdmin,
  }), [dischargeType, balance, reason, causeOfDeath, referralReason, referredTo, authorisedByMemberId, canWriteOffDues, labWriteOffPending, confirmedDodIso, admission.admitted_at, admission.invoice_fy, isAdmin, moneyHidden]);

  const oldestRefundable = refundableDeposits[0] || null;

  const applyServerError = (res) => {
    const mapped = res.code === ADMISSION_ACCESS_ERROR_CODE.NOT_TREATING_DOCTOR
      ? NOT_TREATING_DOCTOR_DISCHARGE_COPY
      : dischargeErrorMessage(res.code, res, { moneyHidden });
    setError(mapped || res.error || 'That action could not be completed.');
    // R3-4/S4 AC3 — UNSETTLED_LAB_CREDIT carries the open credit entries; keep
    // them alongside the mapped headline so the banner can list WHAT is unpaid.
    setErrorEntries(!moneyHidden && res.code === DISCHARGE_ERROR_CODE.UNSETTLED_LAB_CREDIT && Array.isArray(res.credit_entries) ? res.credit_entries : null);
    const late = res.code === DISCHARGE_ERROR_CODE.CHARGES_AFTER_DISCHARGE_DATE;
    setLateCharges(late ? chargesAfterDischargeDateItems(res.charges) : null);
    setLateDod(late ? res.discharge_date || null : null);
    setLineActions(late ? defaultLineActions(chargesAfterDischargeDateItems(res.charges)) : {});
    setMoveError(null);
    if (res.balance_paise != null) setBalance(Math.round(Number(res.balance_paise) || 0));
    if (res.unsettled_lab_paise != null) setUnsettledLabPaise(Math.round(Number(res.unsettled_lab_paise) || 0));
    // BUGFIX/lama-discharge round 2 (R1/R5) — reveal (sticky) the lab
    // write-off exception ONLY for an actor who actually holds WRITE_OFF_DUES
    // (receptionist behaviour must not change — canWriteOffDues=false never
    // sets this). `balance` (this render's cash balance) already reflects
    // what the server saw, so its sign safely stands in for the server's
    // `balance_paise <= 0` half of the R5 condition when a refusal omits it
    // (`AuthorisedByRequired` always does). Three refusals prove the same
    // server state (`absorb_lab` — verified in discharge_service.py):
    //   - AUTHORISED_BY_REQUIRED: the write-off gate itself (no extras).
    //   - REASON_REQUIRED for DECEASED: fires from the SAME absorb_lab block,
    //     strictly before the authoriser check — DECEASED needs Reason
    //     revealed FIRST or it never even reaches the authoriser gate.
    //   - NOT_PERMITTED_DUES with a positive lab amount: the rare actor who
    //     holds WRITE_OFF_DUES without DISCHARGE_WITH_DUES (unreachable under
    //     today's role bundling, kept for completeness/CONTRACT symmetry).
    if (canWriteOffDues && gate.duesEligible && gate.balanceKnown && balance <= 0) {
      const lab = Number(res.unsettled_lab_paise);
      const labKnownPositive = Number.isFinite(lab) && lab > 0;
      const isAuthorisedByRequired = res.code === DISCHARGE_ERROR_CODE.AUTHORISED_BY_REQUIRED;
      const isDeceasedReasonRequired = res.code === DISCHARGE_ERROR_CODE.REASON_REQUIRED
        && dischargeType === DISCHARGE_TYPE.DECEASED;
      const isNotPermittedWithLab = res.code === DISCHARGE_ERROR_CODE.NOT_PERMITTED_DUES && labKnownPositive;
      if (isAuthorisedByRequired || isDeceasedReasonRequired || isNotPermittedWithLab) {
        setLabWriteOffPending(true);
      }
    }
  };

  const handleCollectSubmit = async (body) => {
    const res = await onCollectDeposit(body);
    if (res.success && res.data && res.data.balance_paise_after != null) {
      setBalance(Math.round(Number(res.data.balance_paise_after) || 0));
    }
    return res;
  };

  const handleReturnAdvanceSubmit = async (body) => {
    if (!oldestRefundable) return { success: false, error: 'No refundable deposit found on this admission.' };
    const res = await onReturnAdvance(oldestRefundable.payment_id, body);
    if (res.success && res.data && res.data.balance_paise_after != null) {
      setBalance(Math.round(Number(res.data.balance_paise_after) || 0));
    }
    return res;
  };

  const buildCompleteBody = () => {
    const body = { discharge_type: dischargeType };
    // bk-584 reversed (2026-08-29) — DECEASED now REQUIRES a reason on the
    // SAME write-off path LAMA/DAMA already require one on (`gate.
    // reasonRequired` covers this); a zero-balance death still shows/sends
    // no box, but if the desk typed one anyway it is still worth sending —
    // never silently dropped.
    if (gate.reasonRequired || reason.trim()) body.reason = reason.trim();
    if (gate.referralRequired) {
      body.referral_reason = referralReason.trim();
      // ASSUMPTION (contract silent on a dedicated facility field): the
      // required "Referred to" facility name rides in the optional
      // `referral_note` — see dischargeModel.js JSDoc + integration-notes.md.
      body.referral_note = referredTo.trim();
    }
    if (gate.causeOfDeathRequired) {
      body.cause_of_death = {
        immediate_cause: causeOfDeath.immediate_cause.trim(),
        antecedent_cause: causeOfDeath.antecedent_cause.trim() || null,
        // D2/D3 — while `deathLocked`, `causeOfDeath.death_at` already
        // carries the RECORD's own tz-aware instant (seeded verbatim above,
        // never rendered into an editable control) — resend unchanged.
        // Otherwise it's the NAIVE local wall-clock string straight off
        // `dc-cod-death-at` (a `datetime-local` input, same as
        // `RecordDeathDialog`'s `record-death-at`) and needs the SAME
        // boundary conversion.
        death_at: deathLocked ? causeOfDeath.death_at : new Date(causeOfDeath.death_at).toISOString(),
      };
    }
    // O3 — the write-off exception now submits `authorised_by_member_id` (a
    // staff PICKER selection); the server resolves the display name. The retired
    // free-text `authorised_by` / soft `dues_ack_refused` are no longer sent.
    if (gate.authorisedByRequired) body.authorised_by_member_id = authorisedByMemberId.trim();
    return body;
  };

  // ── STEP 1 — Initiate discharge (its OWN action; calls onInitiate ONLY) ────
  const runInitiate = async () => {
    // Single-flight: `busy` is set before the await and the button is disabled
    // while it holds — one POST per click, never a second in-flight initiate.
    if (busy) return;
    setBusy(true);
    setError(null);
    setErrorEntries(null);
    setLateCharges(null);
    setLateNotice(null);
    setMoveError(null);
    // C1 — ALWAYS carries `discharge_at` (+ the chosen reason sentence when backdated).
    // LAMA/DAMA "Now" re-reads the clock at submit so a long-open dialog never records a stale "now".
    const sendIso = walkOutNow ? new Date().toISOString() : dodIso;
    // Rebuild the sentence from the FINAL date now (never reuse one built from an earlier date).
    const finalReason = resolveDodReason(pickedKey, dodReasonOther, sendIso, { signer: dodSigner, staff: staffName });
    const r1 = await onInitiate(buildInitiateBody({ valueIso: sendIso, reason: finalReason.text, reasonCode: finalReason.code }));
    setBusy(false);
    if (!r1.success) { applyServerError(r1); return r1; }
    // Flip the dialog to step 2 in-place: adopt the server status + the minted
    // invoice number (drives the Interim Bill → Invoice title flip).
    if (r1.data) {
      if (r1.data.status) setCurrentStatus(r1.data.status);
      if (r1.data.invoice_number_printed != null) setInvoiceNumberPrinted(r1.data.invoice_number_printed);
      // FIX-17 — on a CASHLESS stay the server APPLIES the insurer-approved
      // amount AT INITIATE: `initiate_discharge` mints the ONE
      // INSURANCE_SETTLEMENT ledger row and only then reads the admission back
      // (consistent), so this echo's `balance_paise` already IS the new
      // payable (charges − approved − deposits). Adopt it — the SAME "refresh
      // only from a written server value" rule the collect / return-advance
      // handlers above follow, never a client-side subtraction. Without this
      // step 2 kept quoting the pre-settlement figure until the dialog was
      // closed and reopened (the owner's bug). An echo WITHOUT the field
      // leaves the figure untouched — never blanked, never faked to zero.
      if (r1.data.balance_paise != null) setBalance(Math.round(Number(r1.data.balance_paise) || 0));
    }
    // Read the recorded value back off the SERVER's row (never the local
    // field) so the confirm sentence states what was actually stored.
    setInitiatedAt((r1.data && r1.data.planned_discharge_at) || dodIso);
    // The FULL response rides out so the parent can swap every plan
    // affordance (4 chips + the Details row) to the confirmed-date ones in
    // the SAME render — a local merge, never a refetch.
    if (typeof onInitiated === 'function') onInitiated(r1.data);
  };

  // Apply the desk's per-line Remove / Move (ONE POST), then re-submit Initiate
  // automatically (ONE POST). Single-flight: a ref blocks a same-tick double
  // click; `busy` disables every button meanwhile. A stale list (422
  // ACTION_NOT_ALLOWED / 409 CHARGES_CHANGED) is reloaded via Initiate, which
  // returns the current refusal list.
  const fixBody = lateCharges
    ? buildFixChargesBody({ dischargeDate: lateDod, items: lateCharges, actions: lineActions, reasonCode: moveReason, otherText: moveOther })
    : null;
  const runFixAndInitiate = async () => {
    if (!fixBody || busy || moveInFlight.current) return;
    moveInFlight.current = true;
    setBusy(true);
    setMoveError(null);
    try {
      const res = await onFixCharges(fixBody);
      setBusy(false);
      if (!res || !res.success) {
        if (FIX_CHARGES_RELOAD_CODES.includes(res && res.code)) {
          const r = await runInitiate();
          if (r && !r.success) setLateNotice(FIX_CHARGES_RELOAD_COPY);
        } else {
          setMoveError(fixChargesErrorCopy(res && res.code, role));
        }
        return;
      }
      await runInitiate();
    } finally {
      moveInFlight.current = false;
    }
  };

  // ── STEP 2 — Complete discharge (NO initiate leg; clear→complete + gate) ───
  const runComplete = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setErrorEntries(null);
    setLateCharges(null);
    // DECEASED (bk-752) always completes directly, with no initiate/clear
    // leg, from any of the four statuses in `DECEASED_FROM_STATUSES`.
    // Every other type reaches Complete only AFTER Initiate (step 1) already
    // fired, so from DISCHARGE_INITIATED we clear first, then complete.
    if (dischargeType !== DISCHARGE_TYPE.DECEASED && currentStatus === ADMISSION_STATUS.DISCHARGE_INITIATED) {
      const r2 = await onClear();
      if (!r2.success) { setBusy(false); applyServerError(r2); return; }
      // bk-747 — adopt the SERVER's post-clear status (never re-derive it)
      // so a retry after a later `complete` failure (e.g. AUTHORISED_BY_REQUIRED
      // revealed by the sticky write-off gate) does not re-fire `clear` against
      // an admission the server has already moved past DISCHARGE_INITIATED —
      // that second `clear` is refused (INVALID_TRANSITION) and the retry
      // never even reaches `complete`, dead-ending the dialog.
      setCurrentStatus((r2.data && r2.data.status) || ADMISSION_STATUS.BILLING_CLEARANCE);
    }
    const r3 = await onComplete(buildCompleteBody());
    setBusy(false);
    if (r3.success) { setConfirming(false); setSuccess(r3.data); }
    else applyServerError(r3);
  };

  // Death time for the confirm line / read-only date: the record's own instant when
  // locked, else the typed local wall-clock (invalid -> null).
  const deathAtIso = (() => {
    if (!causeOfDeath.death_at) return null;
    const ms = Date.parse(causeOfDeath.death_at);
    return Number.isNaN(ms) ? null : new Date(ms).toISOString();
  })();

  const confirmation = dischargeType
    ? buildCompleteConfirmation({
      dischargeType, patientName: admission.patient_name, gate, unsettledLabPaise,
      confirmedDischargeAt: confirmedDodIso,
      deathAt: deathAtIso, fyAnchor: admission.invoice_fy,
    })
    : null;

  // ── Success: bill/summary/dues-ack hand-off (§7) ────────────────────────
  if (success) {
    const billNo = success.bill && success.bill.bill_number_printed;
    return (
      <div className={overlayCls} data-testid="discharge-dialog">
        <div className={panelCls}>
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Discharge complete</h3>
            <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
          </div>
          <div className="p-4 space-y-3" data-testid="discharge-success">
            <div className="rounded-lg border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 text-teal-800 dark:text-teal-200 text-[13px] p-3 space-y-1">
              {/* S4 — the date that was actually STAMPED, read off the server's
                  own `discharged_at` (never the local choice), so the desk
                  reads what the register will. */}
              {success.admission && success.admission.discharged_at && (
                <div data-testid="discharge-completed-date">{dodCompletedToast(success.admission.discharged_at)}</div>
              )}
              <div>{buildSuccessMessage({ billNumberPrinted: billNo, bedLabel: admission.bed_label })}</div>
            </div>
            {!moneyHidden && success.dues && (
              <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-[13px] p-3 space-y-1" data-testid="discharge-success-dues">
                <div>Dues recorded · Unpaid {formatPaise(success.dues.dues_paise)}</div>
                {Math.round(Number(success.dues.written_off_lab_paise) || 0) > 0 && (
                  <div data-testid="discharge-written-off-lab">
                    Unsettled lab (written off) {formatPaise(success.dues.written_off_lab_paise)}
                  </div>
                )}
              </div>
            )}
            {printError && (
              <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="discharge-print-error">
                {printError}
              </div>
            )}
          </div>
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex flex-wrap justify-end gap-2">
            {canPrint && invoicePrint && (
              <button type="button" className={btnSecondary} onClick={handlePrintInvoiceClick} disabled={printing.invoice} data-testid="discharge-print-invoice">
                {printing.invoice ? 'Generating…' : 'Print invoice'}
              </button>
            )}
            {canPrint && labBillPrint && (
              <button
                type="button"
                className={btnSecondary}
                onClick={handlePrintLabBillClick}
                disabled={printing.labBill || diagnosticsBillDisabled}
                title={diagnosticsBillDisabled ? 'No diagnostics to bill' : undefined}
                data-testid="discharge-print-lab-bill"
              >
                {printing.labBill ? 'Generating…' : diagnosticsBillLabel(labBillStatus)}
              </button>
            )}
            {canPrint && onPrintSummary && (
              <button type="button" className={btnSecondary} onClick={handlePrintSummaryClick} disabled={printing.summary} data-testid="discharge-print-summary">
                {printing.summary ? 'Opening…' : (isDeathOutcome(success.admission && success.admission.status) ? 'Print death summary' : 'Print discharge summary')}
              </button>
            )}
            {/* BE-9 (D3/bk-762) — `render_dues_ack_pdf` refuses 409
                DUES_ACK_REFUSED_ON_DEATH on a DECEASED admission before any
                rendering. Must-not-ship-half-done: the server refusal AND
                this control hidden together, or a receptionist clicks
                through to a raw 409 on an "I undertake to pay" form with
                signature lines — a document this slice widens access to
                (previously admin-only on a residual, now any
                DISCHARGE_PATIENT holder).

                CONTRACT RECONCILE (integration, 2026-08-27): this gate read
                `dues_status !== OPEN`, assumed bijective with a death. It is
                NOT. `discharge_service._complete_response` has a THIRD dues
                branch — DECEASED with a zero charges balance but absorbed
                unsettled LAB credit stamps `dues_status = WRITTEN_OFF`
                (`written_off_lab_paise > 0`, `dues_paise = 0`), which the old
                gate let through straight into the server's 409. Q3's own
                ruling makes that the COMMON death, not an edge: most
                inpatients carry a lab charge on credit. Gate on the SAME
                predicate the server uses — the discharge ended in a death —
                through the shared `isDeathOutcome`, never a money-field
                proxy. */}
            {canPrint && success.dues && !isDeathOutcome(success.dues.discharge_type) && duesAckPrint && <button type="button" className={btnSecondary} onClick={duesAckPrint} data-testid="discharge-print-dues-ack">Print dues acknowledgement</button>}
            <button type="button" className={btnPrimary} onClick={onClose} data-testid="discharge-done">Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={overlayCls} data-testid="discharge-dialog">
      <div className={panelCls}>
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Discharge patient</h3>
          <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
        </div>
        <div className="p-4 space-y-4">
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-[12.5px] text-slate-600 dark:text-slate-300 px-3 py-2 flex items-center justify-between flex-wrap gap-2">
            <span>
              <b className="text-slate-900 dark:text-white">{admission.patient_name || admission.patient_id}</b>
              {' · '}IP no. {admission.ip_number}
              {admission.bed_label ? ` · Bed ${admission.bed_label}` : ''}
              {admission.admitted_at ? ` · Admitted ${admission.admitted_at.slice(0, 10)}` : ''}
            </span>
          </div>

          {/* ── Owner ruling H — no discharge without a treating doctor. Shown
              up top regardless of step (initiate or complete), same ratified
              copy + "Assign doctor" affordance the discharge-summary tab
              uses (single source, `DOCTOR_REQUIRED_DISCHARGE_COPY` — the
                  discharge-flavoured sentence, not the summary one). ─────────────── */}
          {doctorMissing && (
            <div
              className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-3 flex items-center justify-between gap-2 flex-wrap"
              data-testid="discharge-doctor-required-banner"
            >
              <span>{DOCTOR_REQUIRED_DISCHARGE_COPY}</span>
              {onAssignDoctor && (
                <button type="button" className={btnSecondary} onClick={onAssignDoctor} data-testid="discharge-assign-doctor">
                  Assign doctor
                </button>
              )}
            </div>
          )}

          {/* ── Billing document title — flips Interim Bill (IP no.) → Invoice
              (INV#) the moment discharge is initiated and the number is minted
              (drives off `invoice_number_printed`, single source). ─────────── */}
          <div
            className={`rounded-lg border px-3 py-2 flex items-baseline gap-2 flex-wrap ${
              invoiceMinted
                ? 'border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20'
                : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800'
            }`}
            data-testid="discharge-document-title"
          >
            <span className={`text-[13px] font-bold ${invoiceMinted ? 'text-teal-800 dark:text-teal-200' : 'text-slate-800 dark:text-slate-100'}`}>
              {invoiceMinted ? 'Invoice' : 'Interim Bill'}
            </span>
            <span className="text-[12.5px] text-slate-500 dark:text-slate-400 tabular-nums" data-testid="discharge-document-ref">
              {invoiceMinted ? invoiceNumberPrinted : `IP no. ${admission.ip_number}`}
            </span>
          </div>

          {error && (
            <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5 space-y-1.5" data-testid="discharge-error">
              <div>{error}</div>
              {/* R3-4/S4 AC3 — the "WHAT is unpaid" breakdown carried on a 409 UNSETTLED_LAB_CREDIT. */}
              {errorEntries && errorEntries.length > 0 && (
                <ul className="list-disc pl-4 space-y-0.5" data-testid="discharge-unsettled-lab-entries">
                  {errorEntries.map((e) => (
                    <li key={e.credit_entry_id}>{e.test_name} — {formatPaise(e.outstanding_paise)}</li>
                  ))}
                </ul>
              )}
              {lateCharges && lateCharges.length > 0 && (
                <div className="space-y-1.5" data-testid="late-charges-block">
                  {lateNotice && <div className="font-medium" data-testid="late-charges-notice">{lateNotice}</div>}
                  <div>{lateChargesBodyLine(lateDod)}</div>
                  <ul className="space-y-1 max-h-56 overflow-y-auto" data-testid="discharge-late-charges">
                    {lateCharges.map((c) => (
                      <li key={c.key} data-testid={`late-line-${c.id}`}>
                        <div>{c.text}</div>
                        {c.blocked ? (
                          c.blockedCode === FIX_CHARGES_BLOCKED_CODE.LAB_ALREADY_DONE && (
                            <div className="font-medium" data-testid={`late-line-blocked-${c.id}`}>{labDoneLine(c.serviceDate)}</div>
                          )
                        ) : c.allowedActions.length === 1 ? (
                          <div data-testid={`late-line-fixed-${c.id}`}>Will be removed</div>
                        ) : (
                          <div className="flex gap-3" role="radiogroup" aria-label={`Action for ${c.text}`}>
                            {[FIX_CHARGES_ACTION.REMOVE, FIX_CHARGES_ACTION.MOVE].filter((a) => c.allowedActions.includes(a)).map((a) => (
                              <label key={a} className="flex items-center gap-1">
                                <input
                                  type="radio"
                                  name={`late-action-${c.id}`}
                                  checked={lineActions[c.id] === a}
                                  onChange={() => setLineActions((prev) => ({ ...prev, [c.id]: a }))}
                                  disabled={busy}
                                  data-testid={`late-action-${c.id}-${a}`}
                                />
                                {a === FIX_CHARGES_ACTION.REMOVE ? 'Remove' : 'Move'}
                              </label>
                            ))}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                  <div>{LATE_CHARGES_HINT}</div>
                </div>
              )}
              {lateCharges && lateCharges.length > 0 && typeof onFixCharges === 'function' && (
                <div className="space-y-1.5" data-testid="fix-charges-block">
                  <label className={labelCls} htmlFor="move-charges-reason">Why are these charges being fixed?</label>
                  <select
                    id="move-charges-reason"
                    className={inputCls}
                    value={moveReason}
                    onChange={(e) => setMoveReason(e.target.value)}
                    data-testid="move-charges-reason"
                  >
                    <option value="">Select a reason</option>
                    {MOVE_CHARGES_REASON_OPTIONS.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
                  </select>
                  {moveReason === MOVE_CHARGES_REASON.OTHER && (
                    <textarea
                      className={inputCls}
                      rows={2}
                      maxLength={DOD_REASON_MAX_LENGTH}
                      aria-label="Reason for fixing the charges"
                      value={moveOther}
                      onChange={(e) => setMoveOther(e.target.value)}
                      data-testid="move-charges-other"
                    />
                  )}
                  {moveError && <div className="font-medium" data-testid="move-charges-error">{moveError}</div>}
                  <button type="button" className={btnPrimary} onClick={runFixAndInitiate} disabled={busy || !fixBody} data-testid="move-charges-submit">
                    {busy ? 'Fixing…' : `Fix these charges and continue (${fixChargesButtonCount(fixChargesCounts(lateCharges, lineActions))})`}
                  </button>
                </div>
              )}
            </div>
          )}

          <div>
            <label className={labelCls}>Discharge type</label>
            <div className="flex gap-1.5 flex-wrap" role="group" aria-label="Discharge type">
              {offeredTypeOptions.map((t) => {
                // death-record slice (invariant 4/13, D40) — every non-DECEASED
                // type is LOCKED WITH A VISIBLE REASON while a death record is
                // open, never silently hidden or disabled.
                const locked = deathLocked && t !== DISCHARGE_TYPE.DECEASED;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => pickType(t)}
                    disabled={locked}
                    title={locked ? DEATH_TYPE_LOCKED_REASON : undefined}
                    className={locked ? typeChipLocked : (dischargeType === t ? typeChipOn : typeChipOff)}
                    data-testid={`discharge-type-${t}`}
                  >
                    {locked ? '🔒 ' : ''}{DISCHARGE_TYPE_LABELS[t]}
                  </button>
                );
              })}
            </div>
            {!dischargeType && <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">Select discharge type</p>}
            {deathLocked && (
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1" data-testid="discharge-type-locked-reason">
                {DEATH_TYPE_LOCKED_REASON}
              </p>
            )}
          </div>

          {/* ── STEP 1 · Initiate discharge — an ADMITTED/IN_TREATMENT stay is
              first INITIATED (mints the invoice number, flips Interim Bill →
              Invoice, keeps the bill editable). The bed is freed only at
              Complete (step 2). DECEASED (bk-752) always completes directly,
              from any of the four statuses — pick Death above to skip
              straight to Complete. [microcopy → NEEDS-OWNER, PO to ratify] ── */}
          {inInitiateStep && (
            <div className="rounded-lg border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 text-teal-800 dark:text-teal-200 text-[12.5px] p-3 space-y-1.5" data-testid="discharge-initiate-panel">
              <div className="font-semibold">Step 1 of 2 — Initiate discharge</div>
              <div>Initiating issues the invoice number and switches the bill from an Interim Bill to a numbered Invoice. The bill stays editable — you can still add charges.</div>
              <div>The bed is freed only when you complete the discharge (step 2).</div>
            </div>
          )}

          {/* ── S1 Screen A · the confirm step — the discharge date is a
              DECISION taken here, not a leftover plan read at Complete. The
              field pre-fills from the admission's plan (helper state 1) else
              `now` in IST (helper state 2) and is editable either way; the C2
              ladder re-evaluates live on every edit and the action button
              below states exactly ONE reason. The discharge TYPE decides
              whether the field exists at all. ──────────────────────────── */}
          {dodConfirmVisible && (
            <div className="space-y-2" data-testid="dod-confirm-step">
              <div className="text-[12.5px] font-semibold text-slate-800 dark:text-slate-100">
                {isWalkOut ? DOD_WALK_OUT_HEADING : 'Discharge date & time to record'}
              </div>
              {canRecordDischargeDate && isWalkOut && (
                <div className="flex gap-4 text-[12.5px]">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="dod-walk-out" checked={!walkOutEarlier}
                      onChange={() => {
                        setWalkOutEarlier(false);
                        setDodFields(defaultConfirmDodFields(admission));
                        setDodReasonKey('');
                        setDodReasonOther('');
                      }} data-testid="dod-walk-out-now" />
                    <span>Now ({formatDischargeDateTimeIST(nowIso)})</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="dod-walk-out" checked={walkOutEarlier}
                      onChange={() => setWalkOutEarlier(true)} data-testid="dod-walk-out-earlier" />
                    <span>Earlier</span>
                  </label>
                </div>
              )}
              {canRecordDischargeDate ? (!walkOutNow && (
                <div className="flex gap-2 flex-wrap">
                  <div className="flex-1 min-w-[140px]">
                    <label className={labelCls} htmlFor="dod-confirm-date">Discharge date &amp; time</label>
                    <input
                      id="dod-confirm-date"
                      className={inputCls}
                      type="date"
                      value={dodFields.date}
                      onChange={(e) => setDodFields((f) => ({ ...f, date: e.target.value }))}
                      data-testid="dod-confirm-date"
                    />
                  </div>
                  <div className="flex-1 min-w-[120px]">
                    <label className={labelCls} htmlFor="dod-confirm-time">&nbsp;</label>
                    <input
                      id="dod-confirm-time"
                      className={inputCls}
                      type="time"
                      value={dodFields.time}
                      onChange={(e) => setDodFields((f) => ({ ...f, time: e.target.value }))}
                      data-testid="dod-confirm-time"
                    />
                  </div>
                </div>
              )) : (
                /* AC-NEG — read-only WITH the reason, never a silent no-op. */
                <div className="text-[13px] text-slate-800 dark:text-slate-100" data-testid="dod-confirm-readonly">
                  {formatDischargeDateTimeIST(dodIso)}
                </div>
              )}
              {canRecordDischargeDate && dodDays >= 1 && (
                <div className="space-y-1" data-testid="dod-reason-block">
                  <p className="text-[11px] text-slate-500 dark:text-slate-400" data-testid="dod-backdate-hint">{dodBackdateHint(dodDays)}</p>
                  <label className={labelCls} htmlFor="dod-reason-pick">{isWalkOut ? DOD_WALK_OUT_REASON_LABEL : DOD_REASON_LABEL}</label>
                  <select
                    id="dod-reason-pick"
                    className={inputCls}
                    value={pickedKey}
                    onChange={(e) => setDodReasonKey(e.target.value)}
                    data-testid="dod-reason-pick"
                  >
                    <option value="">Select a reason</option>
                    {reasonOptions.map((o) => (
                      <option key={o.key} value={o.key}>
                        {resolveDodReason(o.key, '', dodIso, { signer: 'patient/attendant', staff: staffName }).text}
                      </option>
                    ))}
                    <option value={REASON_OTHER}>Other</option>
                  </select>
                  {pickedKey === 'LAMA_FORM_SIGNED' && (
                    <select
                      className={inputCls}
                      aria-label="LAMA form signed by"
                      value={dodSigner}
                      onChange={(e) => setDodSigner(e.target.value)}
                      data-testid="dod-reason-signer"
                    >
                      <option value="">LAMA form signed by…</option>
                      {SIGNER_OPTIONS.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
                    </select>
                  )}
                  {pickedKey === REASON_OTHER && (
                    <textarea
                      className={inputCls}
                      rows={2}
                      maxLength={DOD_REASON_MAX_LENGTH}
                      value={dodReasonOther}
                      onChange={(e) => setDodReasonOther(e.target.value)}
                      data-testid="dod-reason-other"
                    />
                  )}
                </div>
              )}
              {/* Two states, ONE element (Copy table). */}
              <p className="text-[11px] text-slate-500 dark:text-slate-400" data-testid="dod-confirm-helper">
                {canRecordDischargeDate
                  ? (isWalkOut ? DOD_WALK_OUT_FINAL_BANNER : DOD_INITIATE_HELPER)
                  : DOD_NO_PERMISSION_REASON}
              </p>
              {canRecordDischargeDate && dodCheck.valid && initiateConfirmLines({ valueIso: dodIso }).map((line, idx) => (
                <p key={line} className="text-[12px] text-slate-600 dark:text-slate-300" data-testid={idx === 0 ? 'dod-confirm-line' : 'dod-confirm-period-line'}>
                  {line}
                </p>
              ))}
            </div>
          )}

          {/* DECEASED never reaches the confirm step (existing death-lock) —
              the reason is stated where the field would have been. */}
          {isDischargeStartable(currentStatus) && dischargeType === DISCHARGE_TYPE.DECEASED && (
            <p className="text-[12.5px] text-slate-600 dark:text-slate-300" data-testid="dod-deceased-lock">{DOD_DECEASED_LOCK_REASON}</p>
          )}

          {/* S1 AC3 — what was actually recorded, read back off the server row. */}
          {initiatedAt && (
            <div className="rounded-lg border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 text-[12.5px] p-3" data-testid="dod-initiated-banner">
              {dodInitiatedToast(initiatedAt)}
            </div>
          )}

          {!inInitiateStep && (
          <>
          {/* ── Print invoice (#8b) — step 2 (DISCHARGE_INITIATED/BILLING_CLEARANCE,
              pre-Complete) already has a minted invoice number but, before this
              fix, the ONLY print-invoice CTA in this dialog lived in the
              post-Complete success branch below — nowhere to print it while the
              bill is still open. Reuses the SAME `onPrintInvoice` handler; guarded
              (like the success-branch button) so it never renders without it. ── */}
          {canPrint && (invoicePrint || labBillPrint || onPrintSummary) && (
            <div className="flex flex-col items-end gap-2">
              <div className="flex justify-end gap-2">
                {/* S5 AC5 — the counter hand-over moment: the summary print is
                    offered right after Initiate (this step) and again after
                    Complete (the success branch above). SAME `onPrintSummary`
                    action + the SAME `runPrint` single-flight — no second
                    fetch path; while it is in flight the button is disabled
                    and says so. */}
                {onPrintSummary && (
                  <button type="button" className={btnSecondary} onClick={handlePrintSummaryClick} disabled={printing.summary} data-testid="discharge-step2-print-summary">
                    {printing.summary ? 'Opening…' : 'Print discharge summary'}
                  </button>
                )}
                {invoicePrint && (
                  <button type="button" className={btnSecondary} onClick={handlePrintInvoiceClick} disabled={printing.invoice} data-testid="discharge-step2-print-invoice">
                    {printing.invoice ? 'Generating…' : 'Print invoice'}
                  </button>
                )}
                {labBillPrint && (
                  <button
                    type="button"
                    className={btnSecondary}
                    onClick={handlePrintLabBillClick}
                    disabled={printing.labBill || diagnosticsBillDisabled}
                    title={diagnosticsBillDisabled ? 'No diagnostics to bill' : undefined}
                    data-testid="discharge-step2-print-lab-bill"
                  >
                    {printing.labBill ? 'Generating…' : diagnosticsBillLabel(labBillStatus)}
                  </button>
                )}
              </div>
              {printError && (
                <div className="w-full rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="discharge-print-error">
                  {printError}
                </div>
              )}
            </div>
          )}

          {/* ── Balance / write-off gate (R3-4 [REVERSAL-3] — hard zero-balance
              gate; LAMA/DAMA/DECEASED alone may bypass a residual via the
              permissioned write-off exception) ─────────────────────────── */}
          {gate.balanceKnown && (gate.writeOffEligible ? (
            gate.writeOffAuthorised ? (
              <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-[12.5px] p-3 space-y-1.5" data-testid="discharge-writeoff-panel">
                <div className="font-semibold">Discharge with write-off (exception)</div>
                <div>{formatPaise(balance)} is unpaid. As an authorised exception, this will be recorded as a write-off (bad debt) — not collected from the patient.</div>
                <div>You can still collect any part of it now — the amount to write off will reduce accordingly.</div>
              </div>
            ) : (
              <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-3 space-y-1" data-testid="discharge-blocked-panel">
                <div>{gate.disabledReason}</div>
                <div className="text-[11px]">Only an admin can authorise a write-off exception. Ask an admin to complete this step.</div>
              </div>
            )
          ) : gate.advanceHeld && gate.duesEligible ? (
            <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-[12.5px] p-3" data-testid="discharge-advance-refusal">
              This patient has {formatPaise(-balance)} of unused advance. Return the advance first — a discharge cannot be completed while the organisation holds the patient's money.
            </div>
          ) : (
            <div className={`rounded-lg border text-[13px] p-3 ${gate.balance.kind === 'zero' ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200' : gate.balance.kind === 'advance' ? 'border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 text-teal-800 dark:text-teal-200' : 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200'}`} data-testid="discharge-balance-banner">
              {gate.balance.kind === 'zero' && 'Settled — nothing to collect or return.'}
              {gate.balance.kind === 'owed' && `${gate.balance.text} is still to be collected.`}
              {gate.balance.kind === 'advance' && `${gate.balance.text} of advance is to be returned to the patient.`}
            </div>
          ))}

          {(gate.offerCollect || gate.offerRefund) && (
            <div className="flex flex-wrap gap-2 items-start">
              {gate.offerCollect && (
                <button type="button" className={btnPrimary} onClick={() => setMoneyDialog('collect')} data-testid="discharge-collect-open">
                  Collect {formatPaise(balance)}
                </button>
              )}
              {gate.offerRefund && (
                <div>
                  <button type="button" className={btnPrimary} onClick={() => setMoneyDialog('refund')} disabled={!canReturnAdvance} data-testid="discharge-refund-open">
                    Return advance {formatPaise(-balance)}
                  </button>
                  {!canReturnAdvance && (
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1" data-testid="discharge-refund-disabled-reason">
                      You do not have permission to return an advance. Ask the front desk or an admin to complete this step.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Reason — LAMA/DAMA always; DECEASED only when a write-off
              (cash residual or absorbed lab) is actually being taken
              (bk-584 reversed 2026-08-29: DECEASED now REQUIRES a reason on
              that path too, mirroring LAMA/DAMA — a zero-balance death
              still needs none, cause of death already covers "why") ── */}
          {(gate.reasonRequired || gate.authorisedByRequired) && (
            <div>
              <label className={labelCls} htmlFor="dc-reason">{gate.reasonRequired ? 'Reason (required)' : 'Reason (optional)'}</label>
              <textarea id="dc-reason" className={inputCls} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="discharge-reason" />
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                {dischargeType === DISCHARGE_TYPE.LAMA && 'Briefly note why the patient is leaving against medical advice.'}
                {dischargeType === DISCHARGE_TYPE.DAMA && 'Briefly note why the patient or attendant requested discharge against medical advice.'}
                {dischargeType === DISCHARGE_TYPE.DECEASED && 'Briefly note the circumstances of the write-off — the amount that could not be collected.'}
              </p>
            </div>
          )}

          {/* ── Authorised by (write-off exception only, R3-4/S4 AC6) — O3: a
              STAFF-MEMBER picker (server resolves the name from the id), never
              free text. ─────────────────────────────────────────────────── */}
          {gate.authorisedByRequired && (
            <div>
              <label className={labelCls} htmlFor="dc-authorised-by">Authorised by (required)</label>
              <select
                id="dc-authorised-by"
                className={inputCls}
                value={authorisedByMemberId}
                onChange={(e) => setAuthorisedByMemberId(e.target.value)}
                data-testid="discharge-authorised-by"
              >
                <option value="">Select the authorising staff member…</option>
                {authorisers.map((m) => (
                  <option key={m.member_id} value={m.member_id}>
                    {m.name}{m.role ? ` — ${STAFF_ROLE_LABELS[m.role] || m.role}` : ''}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                {gate.labWriteOffAuthorised
                  ? 'The staff member authorising the write-off of these unpaid lab charges.'
                  : 'The staff member authorising this write-off exception.'}
              </p>
            </div>
          )}

          {/* ── Cause of death (Death only, always required) — SPLIT while a
              death record exists (invariant 13, THE WORKED EXAMPLE this design
              model is built around): `death_at` + the declaring doctor are
              READ-ONLY, pre-filled from the record (the record is the source
              of truth). `immediate_cause`/`antecedent_cause` stay EDITABLE —
              Variant A's record dialog never captures them and
              `CAUSE_OF_DEATH_REQUIRED` still fires at complete. A wholly
              read-only block would make this admission permanently
              un-dischargeable. ─────────────────────────────────────────── */}
          {gate.causeOfDeathRequired && (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2.5" data-testid="discharge-cause-of-death">
              <div className="text-[12.5px] font-semibold text-slate-800 dark:text-slate-100">Cause of death</div>
              {deathLocked && (
                // [NEEDS-PO] copy, pasted from the ratified mockup state 4.
                <div
                  className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-[12px] p-2.5 space-y-1"
                  data-testid="discharge-death-record-readonly"
                >
                  <div className="flex justify-between gap-2">
                    <span>Date &amp; time of death</span>
                    <b data-testid="discharge-death-record-death-at">
                      {admission.cause_of_death.death_at ? new Date(admission.cause_of_death.death_at).toLocaleString() : '—'}
                    </b>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span>Declaring doctor</span>
                    <b data-testid="discharge-death-record-doctor">{admission.cause_of_death.declaring_doctor_name || '—'}</b>
                  </div>
                  <p className="text-[11px] mt-1">
                    Recorded on the admission — edit on Manage Admission (not here) while the stay is still open.
                  </p>
                </div>
              )}
              <div>
                <label className={labelCls} htmlFor="dc-cod-immediate">Immediate cause of death</label>
                <input id="dc-cod-immediate" className={inputCls} type="text" value={causeOfDeath.immediate_cause}
                  onChange={(e) => setCauseOfDeath((c) => ({ ...c, immediate_cause: e.target.value }))} data-testid="discharge-cod-immediate" />
              </div>
              <div>
                <label className={labelCls} htmlFor="dc-cod-antecedent">Underlying / antecedent cause</label>
                <input id="dc-cod-antecedent" className={inputCls} type="text" value={causeOfDeath.antecedent_cause}
                  onChange={(e) => setCauseOfDeath((c) => ({ ...c, antecedent_cause: e.target.value }))} data-testid="discharge-cod-antecedent" />
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">The condition that led to the immediate cause, if known.</p>
              </div>
              {!deathLocked && (
                <div>
                  <label className={labelCls} htmlFor="dc-cod-death-at">Date &amp; time of death</label>
                  <input id="dc-cod-death-at" className={inputCls} type="datetime-local" value={causeOfDeath.death_at}
                    onChange={(e) => setCauseOfDeath((c) => ({ ...c, death_at: e.target.value }))} data-testid="discharge-cod-death-at" />
                </div>
              )}
            </div>
          )}

          {/* ── Referred fields ────────────────────────────────────────── */}
          {gate.referralRequired && (
            <div className="space-y-2.5" data-testid="discharge-referral-fields">
              <div>
                <label className={labelCls} htmlFor="dc-referred-to">Referred to (required)</label>
                <input id="dc-referred-to" className={inputCls} type="text" value={referredTo} placeholder="Name of the facility the patient is referred to"
                  onChange={(e) => setReferredTo(e.target.value)} data-testid="discharge-referred-to" />
              </div>
              <div>
                <label className={labelCls} htmlFor="dc-referral-reason">Reason for referral (required)</label>
                <textarea id="dc-referral-reason" className={inputCls} rows={2} value={referralReason}
                  onChange={(e) => setReferralReason(e.target.value)} data-testid="discharge-referral-reason" />
              </div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">A referred discharge must be fully settled — dues cannot be recorded.</p>
            </div>
          )}

          {/* ── Discharge date (§1.6, read-only) — single source (D1): the
              SAME resolver + formatter the Complete-step confirm line uses,
              so this field and that line can never disagree. ───────────── */}
          <div>
            <label className={labelCls}>Discharge date &amp; time</label>
            <div className="text-[13px] text-slate-800 dark:text-slate-100">
              {formatDischargeDateTimeIST(resolveDischargeDateTimeIso({
                dischargeType, confirmedDischargeAt: confirmedDodIso,
                deathAt: deathAtIso, fyAnchor: admission.invoice_fy,
              }))}
            </div>
            {/* Business C3 — NON-blocking: today is later than the date finalised at Initiate. */}
            {dischargeType && dischargeType !== DISCHARGE_TYPE.DECEASED && confirmedDodIso && dodDaysBack(confirmedDodIso) >= 1 && (
              <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-1" data-testid="discharge-late-complete-note">
                {LATE_COMPLETE_NOTE(formatDischargeDateTimeIST(confirmedDodIso))}
              </p>
            )}
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">Check that room and bed charges cover the stay up to this date.</p>
          </div>
          </>
          )}
        </div>

        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center gap-3 flex-wrap">
          <span className="text-[11.5px] text-slate-500 dark:text-slate-400 flex-1 min-w-[180px]" data-testid="discharge-gate-reason">
            {/* S1 AC2 — on the confirm step the button states the FIRST
                failure of the C2 ladder (exactly one reason), or the
                no-permission reason; elsewhere the existing complete gate. */}
            {doctorMissing
              ? DOCTOR_REQUIRED_DISCHARGE_COPY
              : (inInitiateStep
                ? (!canRecordDischargeDate ? DOD_NO_PERMISSION_REASON : (dodConfirmVisible && dodCheck.reason) || '')
                : (gate.disabledReason || ''))}
          </span>
          <span className="flex gap-2">
            <button type="button" className={btnSecondary} onClick={onClose}>Close</button>
            {inInitiateStep ? (
              /* STEP 1 button — its OWN action (calls onInitiate with the
                 confirmed date). Distinct from Complete: different label,
                 testid, and handler. Owner ruling H — also disabled with no
                 doctor on the admission; S1 AC2/AC-NEG — and whenever the
                 confirmed date fails the ladder or the actor may not record it. */
              <button
                type="button"
                className={btnPrimary}
                onClick={runInitiate}
                disabled={busy || doctorMissing || !canRecordDischargeDate || (dodConfirmVisible && !dodCheck.valid)}
                data-testid="discharge-initiate"
              >
                {busy ? 'Initiating…' : 'Confirm & initiate discharge'}
              </button>
            ) : (
            <button
              type="button"
              className={btnPrimary}
              onClick={() => {
                setConfirming(true);
              }}
              disabled={!gate.completeEnabled || doctorMissing}
              data-testid="discharge-complete-open"
            >
              {gate.completeLabel}
            </button>
            )}
          </span>
        </div>
      </div>

      {moneyDialog === 'collect' && (
        <TakeDepositDialog
          admission={admission}
          balancePaise={balance}
          onClose={() => setMoneyDialog(null)}
          onSubmit={handleCollectSubmit}
          prefillAmountPaise={balance > 0 ? balance : undefined}
          expectedBalancePaise={balance}
        />
      )}
      {moneyDialog === 'refund' && oldestRefundable && (
        <ReturnAdvanceDialog
          admission={admission}
          parent={oldestRefundable}
          amountPaise={-balance}
          balancePaise={balance}
          onClose={() => setMoneyDialog(null)}
          onSubmit={handleReturnAdvanceSubmit}
        />
      )}

      <ConfirmationModal
        isOpen={!!pendingType}
        title="Change discharge type?"
        message="Details entered for the current type will be cleared."
        ctaLabel="Change type"
        cancelLabel="Keep current type"
        onConfirm={confirmTypeChange}
        onCancel={keepCurrentType}
      />

      {confirmation && (
        <ConfirmationModal
          isOpen={confirming}
          title={confirmation.title}
          message={confirmation.body}
          ctaLabel={busy ? 'Completing…' : confirmation.confirmLabel}
          cancelLabel={confirmation.cancelLabel}
          onConfirm={runComplete}
          onCancel={() => (busy ? null : setConfirming(false))}
        />
      )}
    </div>
  );
}
